/**
 * PagosPorReserva — Vista unificada por reserva con todos sus movimientos.
 *
 * Cada fila es una reserva; se puede expandir para ver:
 *   · Pagos del cliente (bold, transferencia, efectivo, comisión anticipada, etc.)
 *     con voucher asociado si existe.
 *   · Pagos de comisión a la agencia (abonos parciales).
 *   · Liquidación de comisión completa si aplica.
 *
 * Filtros: fecha, agencia, estado, método, texto libre (código/cliente).
 * Read-only — para acciones específicas se usan los otros módulos.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, TextField, MenuItem, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Stack, CircularProgress, IconButton, Collapse, Tooltip,
  FormControl, InputLabel, Select,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PaymentsIcon from '@mui/icons-material/Payments';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import LockIcon from '@mui/icons-material/Lock';
import SearchIcon from '@mui/icons-material/Search';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';

function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

const fmt = (n) => new Intl.NumberFormat('es-CO').format(Math.round(Number(n) || 0));
const fmtFecha = (iso) => {
  if (!iso) return '—';
  try {
    const withZ = /Z|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : iso + 'Z';
    const d = new Date(withZ);
    return d.toLocaleString('es-CO', {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true,
      timeZone: 'America/Bogota',
    });
  } catch { return iso.slice(0, 16); }
};

const hoy = () => new Date().toISOString().slice(0, 10);
const menosDias = (d) => {
  const dt = new Date();
  dt.setDate(dt.getDate() - d);
  return dt.toISOString().slice(0, 10);
};

// Etiqueta amigable por origen/método
const labelOrigen = (origen, metodo) => {
  const o = (origen || '').toLowerCase();
  const m = (metodo || '').toLowerCase();
  if (o === 'comision_anticipada') return 'Comisión anticipada (agencia retuvo)';
  if (o === 'muelle') return `Cobro muelle · ${metodo || '—'}`;
  if (o === 'cruce') return 'Cruce contable';
  if (o === 'pago_directo_cliente') return `Cliente · ${metodo || '—'}`;
  if (m === 'bold_link') return 'Bold link';
  return `${origen || '—'}${metodo ? ` · ${metodo}` : ''}`;
};

// Chip de estado de la reserva
const chipEstado = (r) => {
  const est = (r.estado || '').toLowerCase();
  const emb = (r.estado_embarque || '').toLowerCase();
  if (est === 'cancelada') return <Chip size="small" label="Cancelada" sx={{ bgcolor: '#fee2e2', color: '#991b1b', fontSize: 10 }} />;
  if (est === 'no_show' || emb === 'no_show') return <Chip size="small" label="No-show" sx={{ bgcolor: '#fef3c7', color: '#92400e', fontSize: 10 }} />;
  if ((r.saldo_pendiente || 0) === 0) return <Chip size="small" label="Pagada" sx={{ bgcolor: '#dcfce7', color: '#166534', fontSize: 10 }} />;
  return <Chip size="small" label="Con saldo" sx={{ bgcolor: '#fef3c7', color: '#a16207', fontSize: 10 }} />;
};

export default function PagosPorReserva() {
  const [fechaDesde, setFechaDesde] = useState(menosDias(30));
  const [fechaHasta, setFechaHasta] = useState(hoy());
  const [agenciaId, setAgenciaId] = useState('');
  const [estado, setEstado] = useState('');
  const [metodo, setMetodo] = useState('');
  const [q, setQ] = useState('');
  const [agencias, setAgencias] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandidos, setExpandidos] = useState(new Set());

  const toggle = (id) => {
    const s = new Set(expandidos);
    if (s.has(id)) s.delete(id); else s.add(id);
    setExpandidos(s);
  };

  // Agencias para el dropdown
  useEffect(() => {
    fetch(`${API_BASE}/turismo/agencias?empresa_id=1`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(list => {
        const arr = Array.isArray(list) ? list : (list?.agencias || []);
        setAgencias([...arr].sort((a, b) => (a.nombre || '').localeCompare(b.nombre || '')));
      })
      .catch(() => setAgencias([]));
  }, []);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ empresa_id: '1' });
      if (fechaDesde) params.append('fecha_desde', fechaDesde);
      if (fechaHasta) params.append('fecha_hasta', fechaHasta);
      if (agenciaId) params.append('agencia_id', agenciaId);
      if (estado) params.append('estado', estado);
      if (metodo) params.append('metodo', metodo);
      if (q && q.trim()) params.append('q', q.trim());
      const r = await fetch(`${API_BASE}/turismo/finanzas/pagos-por-reserva?${params}`, { headers: getAuthHeaders() });
      const d = await r.json();
      setItems(d.items || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [fechaDesde, fechaHasta, agenciaId, estado, metodo, q]);

  useEffect(() => { cargar(); }, [cargar]);

  const totales = useMemo(() => ({
    reservas: items.length,
    // PVP cobrado = suma real de pagos del cliente (lo que salió del bolsillo del turista).
    pvp_cobrado: items.reduce((s, i) => s + (i.totales?.cobrado_cliente || 0), 0),
    // Neto Entretur = suma de (precio_total − descuento − comisión) por reserva.
    // Refleja el margen contable, independiente de cuánta comisión ya se pagó.
    neto_entretur: items.reduce((s, i) => s + (i.totales?.neto_entretur || 0), 0),
  }), [items]);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <PaymentsIcon sx={{ fontSize: 32, color: '#0d9488' }} />
        <Box>
          <Typography sx={{ fontSize: 22, fontWeight: 700, color: '#0f172a' }}>
            Pagos por reserva
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: '#64748b' }}>
            Cada reserva con su detalle completo de pagos: cliente, comisión y liquidación.
          </Typography>
        </Box>
      </Stack>

      {/* Filtros */}
      <Card sx={{ mb: 2, borderRadius: 3 }}>
        <CardContent sx={{ py: 1.5 }}>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" alignItems="center" useFlexGap>
            <TextField type="date" label="Desde" size="small" InputLabelProps={{ shrink: true }}
              value={fechaDesde} onChange={e => setFechaDesde(e.target.value)} sx={{ minWidth: 160 }} />
            <TextField type="date" label="Hasta" size="small" InputLabelProps={{ shrink: true }}
              value={fechaHasta} onChange={e => setFechaHasta(e.target.value)} sx={{ minWidth: 160 }} />
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Agencia</InputLabel>
              <Select label="Agencia" value={agenciaId} onChange={e => setAgenciaId(e.target.value)}>
                <MenuItem value=""><em>Todas</em></MenuItem>
                {agencias.map(a => <MenuItem key={a.id} value={a.id}>{a.nombre}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Estado</InputLabel>
              <Select label="Estado" value={estado} onChange={e => setEstado(e.target.value)}>
                <MenuItem value=""><em>Todas</em></MenuItem>
                <MenuItem value="pagada">Pagadas</MenuItem>
                <MenuItem value="con_saldo">Con saldo</MenuItem>
                <MenuItem value="no_show">No-show</MenuItem>
                <MenuItem value="cancelada">Canceladas</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Método</InputLabel>
              <Select label="Método" value={metodo} onChange={e => setMetodo(e.target.value)}>
                <MenuItem value=""><em>Todos</em></MenuItem>
                <MenuItem value="bold_link">Bold link</MenuItem>
                <MenuItem value="transferencia">Transferencia</MenuItem>
                <MenuItem value="efectivo">Efectivo</MenuItem>
                <MenuItem value="cheque">Cheque</MenuItem>
                <MenuItem value="comision_anticipada">Anticipada agencia</MenuItem>
                <MenuItem value="cruce">Cruce</MenuItem>
              </Select>
            </FormControl>
            <TextField size="small" placeholder="Código o cliente"
              value={q} onChange={e => setQ(e.target.value)}
              InputProps={{ startAdornment: <SearchIcon sx={{ mr: 0.75, color: '#94a3b8', fontSize: 18 }} /> }}
              sx={{ minWidth: 220 }} />
          </Stack>
        </CardContent>
      </Card>

      {/* KPIs — 2 tarjetas financieras (más contador de reservas):
            · PVP cobrado: total real de pagos del cliente.
            · Neto Entretur: PVP lista − descuento − comisión total (margen contable). */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(3, 1fr)', sm: 'repeat(3, 1fr)' },
        gap: 1.5, mb: 2,
      }}>
        <KpiCard label="Reservas" value={totales.reservas} color="#0f172a" />
        <KpiCard label="PVP cobrado" value={`$${fmt(totales.pvp_cobrado)}`} color="#166534" small />
        <KpiCard label="Neto Entretur" value={`$${fmt(totales.neto_entretur)}`} color="#0d9488" small />
      </Box>

      {/* Tabla */}
      <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: '#f8fafc' }}>
            <TableRow>
              <TableCell sx={{ width: 40 }} />
              <TableCell sx={{ fontWeight: 700 }}>Reserva</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Cliente</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Agencia</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Fecha salida</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Precio</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Cobrado</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Saldo</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>Estado</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}
            {!loading && items.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4, color: '#64748b' }}>
                  Sin reservas en el filtro seleccionado.
                </TableCell>
              </TableRow>
            )}
            {!loading && items.map((it) => {
              const r = it.reserva;
              const open = expandidos.has(r.id);
              return (
                <React.Fragment key={r.id}>
                  <TableRow hover sx={{
                    '& > *': { borderBottom: 'unset' },
                    cursor: 'pointer',
                    bgcolor: open ? '#f0fdfa' : undefined,
                  }}
                    onClick={() => toggle(r.id)}>
                    <TableCell>
                      <IconButton size="small" sx={{ color: open ? '#0d9488' : undefined }}>
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 12.5 }}>{r.codigo_reserva}</TableCell>
                    <TableCell sx={{ fontSize: 12.5 }}>{r.nombre_cliente}</TableCell>
                    <TableCell sx={{ fontSize: 12.5, color: '#475569' }}>{r.agencia_nombre || '—'}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{r.fecha_salida || '—'}</TableCell>
                    <TableCell align="right" sx={{ fontSize: 12.5 }}>${fmt(r.precio_total)}</TableCell>
                    <TableCell align="right" sx={{ fontSize: 12.5, color: '#166534', fontWeight: 600 }}>${fmt(it.totales.cobrado_cliente)}</TableCell>
                    <TableCell align="right" sx={{ fontSize: 12.5, color: r.saldo_pendiente > 0 ? '#d97706' : '#94a3b8', fontWeight: r.saldo_pendiente > 0 ? 700 : 400 }}>
                      ${fmt(r.saldo_pendiente)}
                    </TableCell>
                    <TableCell align="center">{chipEstado(r)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={9} sx={{ py: 0, borderBottom: open ? '1px solid #e2e8f0' : 'none' }}>
                      <Collapse in={open} unmountOnExit>
                        <DetallePagos item={it} />
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function KpiCard({ label, value, color, small = false }) {
  return (
    <Card sx={{ borderRadius: 3, borderLeft: `4px solid ${color}` }}>
      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
        <Typography sx={{ fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: small ? 18 : 22, fontWeight: 700, color, mt: 0.5 }}>
          {value}
        </Typography>
      </CardContent>
    </Card>
  );
}

function DetallePagos({ item }) {
  const pagosCli = item.pagos_cliente || [];
  const pagosCom = item.pagos_comision || [];
  const liq = item.liquidacion;
  const r = item.reserva;
  return (
    <Box sx={{
      p: 2,
      bgcolor: '#f0fdfa',                    // mismo teal-50 que en Comisiones pagadas
      borderLeft: '4px solid #0d9488',
    }}>
      {/* Pagos del cliente */}
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0f172a', mb: 0.75, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Pagos del cliente ({pagosCli.length})
      </Typography>
      {pagosCli.length === 0 ? (
        <Typography sx={{ fontSize: 12, color: '#64748b', mb: 2 }}>Sin pagos del cliente registrados.</Typography>
      ) : (
        <Table size="small" sx={{ bgcolor: '#fff', borderRadius: 2, mb: 2 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Fecha</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Concepto</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11 }}>Monto</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Referencia</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Aplicado por</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="center">Evidencia</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pagosCli.map(p => (
              <TableRow key={p.id}>
                <TableCell sx={{ fontSize: 11.5 }}>{fmtFecha(p.fecha)}</TableCell>
                <TableCell sx={{ fontSize: 11.5 }}>{labelOrigen(p.origen, p.metodo_pago)}</TableCell>
                <TableCell align="right" sx={{ fontSize: 12, fontWeight: 700, color: '#166534' }}>${fmt(p.monto)}</TableCell>
                <TableCell sx={{ fontSize: 11, color: '#64748b' }}>{p.voucher?.referencia || '—'}</TableCell>
                <TableCell sx={{ fontSize: 11, color: '#64748b' }}>{p.aplicado_por || '—'}</TableCell>
                <TableCell align="center"><EvidenciaCell voucher={p.voucher} origen={p.origen} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Cuadre visual: PVP lista → descuento → cobrado → comisión → neto Entretur */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Chip size="small" label={`PVP lista: $${fmt(r.precio_total)}`} sx={{ bgcolor: '#e0f2fe', color: '#075985', fontWeight: 600 }} />
        {(r.descuento_comercial || 0) > 0 && (
          <Chip size="small" label={`Desc. comercial: −$${fmt(r.descuento_comercial)}`} sx={{ bgcolor: '#fef3c7', color: '#a16207', fontWeight: 600 }} />
        )}
        <Chip size="small" label={`Cobrado: $${fmt(item.totales.cobrado_cliente)}`} sx={{ bgcolor: '#dcfce7', color: '#166534', fontWeight: 700 }} />
        <Chip size="small" label={`Saldo: $${fmt(r.saldo_pendiente)}`}
          sx={{ bgcolor: r.saldo_pendiente > 0 ? '#fef3c7' : '#f1f5f9', color: r.saldo_pendiente > 0 ? '#a16207' : '#64748b', fontWeight: 700 }} />
        <Chip size="small" label={`− Comisión: $${fmt(r.comision_valor)}`} sx={{ bgcolor: '#f1f5f9', color: '#475569', fontWeight: 600 }} />
        <Chip size="small" label={`= Neto Entretur: $${fmt(r.neto_entretur)}`} sx={{ bgcolor: '#ccfbf1', color: '#0d9488', fontWeight: 800 }} />
      </Box>

      {/* Pagos de comisión */}
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0f172a', mb: 0.75, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Pagos de comisión a la agencia ({pagosCom.length})
      </Typography>
      {pagosCom.length === 0 && !liq ? (
        <Typography sx={{ fontSize: 12, color: '#64748b', mb: 1 }}>Sin abonos ni liquidación de comisión.</Typography>
      ) : (
        <Table size="small" sx={{ bgcolor: '#fff', borderRadius: 2 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Fecha</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Tipo</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11 }}>Monto</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Método</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Referencia</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="center">Evidencia</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pagosCom.map(p => (
              <TableRow key={`pc-${p.id}`}>
                <TableCell sx={{ fontSize: 11.5 }}>{fmtFecha(p.fecha || p.fecha_pago)}</TableCell>
                <TableCell sx={{ fontSize: 11.5 }}>Abono parcial</TableCell>
                <TableCell align="right" sx={{ fontSize: 12, fontWeight: 700, color: '#0d9488' }}>${fmt(p.monto)}</TableCell>
                <TableCell sx={{ fontSize: 11 }}>{p.metodo_pago || '—'}</TableCell>
                <TableCell sx={{ fontSize: 11, color: '#64748b' }}>{p.referencia || '—'}</TableCell>
                <TableCell align="center"><EvidenciaLink url={p.comprobante_url} label="Comprobante" /></TableCell>
              </TableRow>
            ))}
            {liq && (
              <TableRow>
                <TableCell sx={{ fontSize: 11.5 }}>{fmtFecha(liq.fecha_pago)}</TableCell>
                <TableCell sx={{ fontSize: 11.5, fontWeight: 700 }}>Liquidación completa</TableCell>
                <TableCell align="right" sx={{ fontSize: 12, fontWeight: 700, color: '#0d9488' }}>${fmt(liq.total_comisiones)}</TableCell>
                <TableCell sx={{ fontSize: 11 }}>{liq.metodo_pago || '—'}</TableCell>
                <TableCell sx={{ fontSize: 11, color: '#64748b' }}>{liq.referencia || '—'}</TableCell>
                <TableCell align="center"><EvidenciaLink url={liq.comprobante_url} label="Comprobante" /></TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}

function EvidenciaCell({ voucher, origen }) {
  if (origen === 'comision_anticipada') {
    return (
      <Tooltip title="La agencia retuvo su comisión al cobrar al cliente. No genera voucher.">
        <Chip size="small" label="Retención" sx={{ bgcolor: '#fefce8', color: '#a16207', fontSize: 10, fontWeight: 600 }} />
      </Tooltip>
    );
  }
  if (!voucher) {
    return (
      <Tooltip title="Sin voucher asociado">
        <Chip size="small" label="—" sx={{ bgcolor: '#fee2e2', color: '#991b1b', fontSize: 10, fontWeight: 600 }} />
      </Tooltip>
    );
  }
  const href = voucher.imagen_url?.startsWith('http')
    ? voucher.imagen_url
    : `${API_BASE.replace(/\/api$/, '')}${voucher.imagen_url || ''}`;
  const isImage = voucher.imagen_url && /\.(jpe?g|png|webp|gif)(\?|$)/i.test(voucher.imagen_url);
  if (isImage) {
    return (
      <Tooltip title={`Ver voucher #${voucher.id} (${voucher.estado})`}>
        <Box component="a" href={href} target="_blank" rel="noopener"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, textDecoration: 'none', color: '#0891b2' }}>
          <Box component="img" src={href} alt={`v${voucher.id}`}
            sx={{ width: 28, height: 28, borderRadius: 1, objectFit: 'cover', border: '1px solid #e2e8f0' }} />
          <Typography sx={{ fontSize: 10, fontWeight: 600 }}>#{voucher.id}</Typography>
        </Box>
      </Tooltip>
    );
  }
  return <EvidenciaLink url={voucher.imagen_url} label={`Voucher #${voucher.id}`} />;
}

function EvidenciaLink({ url, label }) {
  if (!url) {
    return <Typography sx={{ fontSize: 10, color: '#94a3b8' }}>—</Typography>;
  }
  const href = url.startsWith('http') ? url : `${API_BASE.replace(/\/api$/, '')}${url}`;
  return (
    <Tooltip title={`Ver ${label.toLowerCase()}`}>
      <Chip clickable component="a" href={href} target="_blank" rel="noopener"
        icon={<ReceiptLongIcon sx={{ fontSize: 12 }} />}
        label={label} size="small"
        sx={{ bgcolor: '#e0f2fe', color: '#0369a1', fontWeight: 600, fontSize: 10 }} />
    </Tooltip>
  );
}
